// src/pages/FinancePage/components/funnel-detail/CpaModal.tsx

import { useState, useEffect, useMemo } from 'react';
import { X, Target, TrendingDown, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatPercentage } from './utils';

type Props = {
    currentCpa: number;
    targetCpa: number;
    maxProfitableCpa: number;
    effectiveTargetPrice: number;     // precio efectivo objetivo, para el benchmark del 20%
    onTargetChange: (newCpa: number) => void;
    onClose: () => void;
};

const ASSUMED_IMPRESSIONS = 100000;
const BENCHMARK_CPA_PCT_OF_PRICE = 0.20; // 20% del precio efectivo

export function CpaModal({ currentCpa, targetCpa, maxProfitableCpa, effectiveTargetPrice, onTargetChange, onClose }: Props) {
    // Benchmark sugerido: 20% del precio efectivo
    const benchmarkCpa = useMemo(() => Math.round(effectiveTargetPrice * BENCHMARK_CPA_PCT_OF_PRICE), [effectiveTargetPrice]);

    // Defaults retro-calculados desde el CPA actual.
    // Punto de partida sensato: spend $1.000.000, CTR 1.5%, checkout 8%, y purchaseRate se ajusta para que el CPA del simulador = currentCpa.
    const defaults = useMemo(() => {
        const adSpend = 1_000_000;
        const ctr = 1.5;
        const checkoutRate = 8;
        // CPA = adSpend / (impresiones × ctr × checkoutRate × purchaseRate)
        // → purchaseRate = adSpend / (CPA × impresiones × ctr × checkoutRate)
        const denom = ASSUMED_IMPRESSIONS * (ctr / 100) * (checkoutRate / 100);
        const idealPurchases = currentCpa > 0 ? adSpend / currentCpa : 30;
        let purchaseRate = denom > 0 ? (idealPurchases / denom) * 100 : 12;
        // Clamp a rango realista del slider 1-50
        purchaseRate = Math.max(1, Math.min(50, purchaseRate));
        return { adSpend, ctr, checkoutRate, purchaseRate: Math.round(purchaseRate * 10) / 10 };
    }, [currentCpa]);

    const [adSpend, setAdSpend] = useState(defaults.adSpend);
    const [ctr, setCtr] = useState(defaults.ctr);
    const [checkoutRate, setCheckoutRate] = useState(defaults.checkoutRate);
    const [purchaseRate, setPurchaseRate] = useState(defaults.purchaseRate);

    // Si benchmarkCpa cambia (porque cambió el precio objetivo afuera), arrancar el draft con benchmark cuando el targetCpa actual es el viejo default
    const initialTarget = targetCpa > 0 ? targetCpa : benchmarkCpa;
    const [draftTarget, setDraftTarget] = useState(initialTarget.toString());

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const clicks = ASSUMED_IMPRESSIONS * (ctr / 100);
    const checkouts = clicks * (checkoutRate / 100);
    const purchases = checkouts * (purchaseRate / 100);
    const simulatedCpa = purchases > 0 ? adSpend / purchases : 0;

    const isProfitable = simulatedCpa <= maxProfitableCpa;
    const headroom = maxProfitableCpa - simulatedCpa;

    const commitTarget = () => {
        const parsed = parseFloat(draftTarget.replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed) && parsed > 0) onTargetChange(parsed);
        onClose();
    };

    const useSimulatedAsTarget = () => {
        onTargetChange(Math.round(simulatedCpa));
        onClose();
    };

    const useBenchmarkAsTarget = () => {
        setDraftTarget(benchmarkCpa.toString());
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 tabular-nums" onClick={onClose}>
            <div
                className="max-h-[92vh] w-full max-w-[860px] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">CPA objetivo</h2>
                        <p className="text-sm text-gray-500 mt-1">Ajusta cuánto puede costar cada compra sin romper el margen del embudo.</p>
                    </div>
                    <button onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:bg-gray-50 hover:text-gray-600">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {/* Tres números clave */}
                <section className="px-6 py-5 grid gap-3 border-b border-gray-100 md:grid-cols-3">
                    <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                            <TrendingDown className="w-4 h-4 text-gray-500" />
                            <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Tu CPA actual</p>
                        </div>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(currentCpa)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">lo que pagas hoy por cada venta</p>
                    </div>

                    <div className="rounded-lg border border-red-100 bg-red-50/80 p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                            <AlertTriangle className="w-4 h-4 text-red-700" />
                            <p className="text-[11px] uppercase tracking-wide text-red-700 font-semibold">CPA máximo rentable</p>
                        </div>
                        <p className="text-xl font-bold text-red-900">{formatCurrency(maxProfitableCpa)}</p>
                        <p className="text-[11px] text-red-700 mt-0.5">por encima de este valor el margen se vuelve negativo</p>
                    </div>

                    <div className="rounded-lg border border-blue-100 bg-blue-50/80 p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                            <Target className="w-4 h-4 text-blue-700" />
                            <p className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Tu objetivo</p>
                        </div>
                        <input
                            type="text"
                            value={draftTarget}
                            onChange={(e) => setDraftTarget(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitTarget(); }}
                            className="w-full -mx-1 rounded-lg bg-transparent px-1 text-xl font-bold text-blue-900 outline-none transition focus:bg-white focus:ring-2 focus:ring-blue-100"
                        />
                        <p className="text-[11px] text-blue-700 mt-0.5">
                            recomendado: {formatCurrency(benchmarkCpa)} (20% del precio)
                        </p>
                        {parseFloat(draftTarget.replace(/[^\d.-]/g, '')) !== benchmarkCpa && (
                            <button
                                onClick={useBenchmarkAsTarget}
                                className="text-[11px] text-blue-700 hover:text-blue-900 underline mt-1"
                            >
                                Usar valor recomendado
                            </button>
                        )}
                    </div>
                </section>

                {/* Calculadora */}
                <section className="px-6 py-5 border-b border-gray-100">
                    <div className="flex items-baseline justify-between gap-4 mb-1">
                        <h3 className="text-sm font-bold text-gray-900">Simulador: ¿qué CPA puedo lograr?</h3>
                        <p className="text-[11px] text-gray-500">Mueve los valores y mira cómo cambia tu CPA</p>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                        Estos valores arrancan ajustados a tu situación actual. Si subes el CTR o las conversiones, el CPA baja.
                    </p>

                    <div className="grid gap-x-6 gap-y-4 mb-5 md:grid-cols-2">
                        <SliderField
                            label="Inversión en pauta"
                            hint="Cuánto gastas en Meta este mes"
                            value={adSpend}
                            min={100000}
                            max={5000000}
                            step={50000}
                            unit="currency"
                            onChange={setAdSpend}
                        />
                        <SliderField
                            label="CTR (Click Through Rate)"
                            hint="% de personas que hacen click en tu anuncio"
                            value={ctr}
                            min={0.5}
                            max={5}
                            step={0.1}
                            unit="percentage"
                            onChange={setCtr}
                        />
                        <SliderField
                            label="Llegan al checkout"
                            hint="% de los clicks que llegan a la página de pago"
                            value={checkoutRate}
                            min={1}
                            max={30}
                            step={0.5}
                            unit="percentage"
                            onChange={setCheckoutRate}
                        />
                        <SliderField
                            label="Completan la compra"
                            hint="% del checkout que termina la compra"
                            value={purchaseRate}
                            min={1}
                            max={50}
                            step={0.5}
                            unit="percentage"
                            onChange={setPurchaseRate}
                        />
                    </div>

                    <div className={`rounded-lg border p-4 ${isProfitable ? 'bg-emerald-50/80 border-emerald-200' : 'bg-red-50/80 border-red-200'}`}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-700 mb-1">Con esos valores tu CPA sería</p>
                                <p className={`text-2xl font-bold ${isProfitable ? 'text-emerald-900' : 'text-red-900'}`}>
                                    {formatCurrency(simulatedCpa)}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Con {Math.round(purchases)} compras sobre una inversión de {formatCurrency(adSpend)}
                                </p>
                            </div>
                            <div className="text-right">
                                {isProfitable ? (
                                    <>
                                        <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Te queda margen de</p>
                                        <p className="text-base font-bold text-emerald-900">{formatCurrency(headroom)}</p>
                                        <p className="text-xs text-emerald-700">por cada venta</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-[11px] uppercase tracking-wide text-red-700 font-semibold">Por encima del máximo en</p>
                                        <p className="text-base font-bold text-red-900">{formatCurrency(Math.abs(headroom))}</p>
                                        <p className="text-xs text-red-700">por cada venta</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={useSimulatedAsTarget}
                            className="mt-4 inline-flex h-10 w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-blue-100 hover:bg-blue-50 hover:text-[#2b5ce6]"
                            type="button"
                        >
                            Usar este CPA como mi objetivo
                        </button>
                    </div>
                </section>

                {/* Palancas operativas */}
                <section className="px-6 py-5 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-900 mb-1">Cómo bajar tu CPA en tus campañas</h3>
                    <p className="text-xs text-gray-500 mb-4">Estas son las acciones reales que mueven los números del simulador.</p>

                    <div className="grid gap-3 md:grid-cols-3">
                        <PlaybookGroup
                            title="A quién le muestras los anuncios"
                            hint="Mueve el CTR y la conversión a compra"
                            items={[
                                { name: 'Refina tu audiencia', desc: 'Quita segmentos que no convierten, duplica los que sí.' },
                                { name: 'Remarketing', desc: 'Vuelve a impactar a quienes ya te visitaron. Su CPA suele ser mucho menor.' },
                                { name: 'Audiencias similares (lookalike)', desc: 'A partir de tus mejores clientes, encuentra perfiles parecidos.' },
                                { name: 'Geo y horarios', desc: 'Apaga zonas u horas donde gastas y no conviertes.' },
                            ]}
                        />

                        <PlaybookGroup
                            title="Qué les muestras"
                            hint="Mueve el CTR principalmente"
                            items={[
                                { name: 'Prueba creativos nuevos cada semana', desc: 'La fatiga publicitaria sube el CPA. Rota imágenes y videos.' },
                                { name: 'Mejora copies y llamados a la acción', desc: 'El primer gancho del anuncio es el que define el CTR.' },
                                { name: 'Videos cortos vs imágenes', desc: 'Prueba qué formato performa mejor para tu producto.' },
                            ]}
                        />

                        <PlaybookGroup
                            title="Qué les ofreces"
                            hint="Mueve la conversión a checkout y a compra"
                            items={[
                                { name: 'Optimiza el checkout', desc: 'Pasos sencillos, pocos campos, opciones de pago claras.' },
                                { name: 'Promociones limitadas', desc: 'Descuentos, envío gratis o regalos crean urgencia y suben la conversión.' },
                                { name: 'Mejora la página del producto', desc: 'Imágenes claras, beneficios visibles, prueba social.' },
                            ]}
                        />
                    </div>
                </section>

                <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                        type="button"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={commitTarget}
                        className="inline-flex h-10 items-center justify-center rounded-lg bg-[#2b5ce6] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7]"
                        type="button"
                    >
                        Guardar objetivo
                    </button>
                </div>
            </div>
        </div>
    );
}

function SliderField({ label, hint, value, min, max, step, unit, onChange }: {
    label: string;
    hint: string;
    value: number;
    min: number;
    max: number;
    step: number;
    unit: 'currency' | 'percentage';
    onChange: (v: number) => void;
}) {
    const formatted = unit === 'currency' ? formatCurrency(value) : formatPercentage(value);

    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <label className="text-sm font-semibold text-gray-900">{label}</label>
                <span className="text-sm font-bold text-gray-900 tabular-nums">{formatted}</span>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">{hint}</p>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full accent-[#2b5ce6]"
            />
        </div>
    );
}

function PlaybookGroup({ title, hint, items }: {
    title: string;
    hint: string;
    items: { name: string; desc: string }[];
}) {
    return (
        <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-4">
            <div className="mb-3">
                <h4 className="text-sm font-bold text-gray-900">{title}</h4>
                <span className="mt-1 block text-[11px] text-gray-500">{hint}</span>
            </div>
            <ul className="space-y-2">
                {items.map((item, i) => (
                    <li key={i} className="flex gap-3 text-xs">
                        <span className="text-gray-300 font-bold mt-0.5">·</span>
                        <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.name}</p>
                            <p className="text-gray-600 mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
